import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { Spinner } from '@repo/components';
import { PERMISSIONS, InviteData, LinkData, User } from '@repo/shared/domain';
import { EventEmitter, getVerifyPermission, guardedClient as request } from '@repo/shared/helpers';
import { constructQuery } from '@repo/shared/helpers';
import UsersTab from 'src/parts/tabs/UsersTab';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getGroup } from 'src/api/personal/groups';
import { getGroupUsers, getGroupRoles } from 'src/api/personal/groupDetail';

interface UsersSearch {
	page?: number;
	sort?: string;
	filter?: string;
}

export const Route = createFileRoute('/personal_/groups/$groupId/users')({
	validateSearch: (search: Record<string, unknown>): UsersSearch => ({
		page: (search.page as number) || undefined,
		sort: search.sort as string | undefined,
		filter: search.filter as string | undefined,
	}),
	component: GroupUsersPage,
});

function GroupUsersPage() {
	const { groupId } = Route.useParams();
	const { page, sort, filter } = Route.useSearch();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { config, loading: configLoading } = useConfig();
	const defaultCountry = config?.defaultCountry || 'BE';

	const { data: group, isLoading: groupLoading } = useQuery({
		queryKey: ['personal-group', groupId],
		queryFn: () => getGroup(groupId),
	});

	const query = constructQuery({ page, sort, filter });
	const { data: usersData, isLoading: usersLoading } = useQuery({
		queryKey: ['personal-group-users', groupId, query],
		queryFn: () => getGroupUsers(groupId, query),
		enabled: !!group,
	});

	const { data: roles } = useQuery({
		queryKey: ['personal-group-roles', groupId],
		queryFn: () => getGroupRoles(groupId),
		enabled: !!group,
	});

	const verifyPermission = authenticatedUser ? getVerifyPermission(authenticatedUser.permissions) : () => false;

	const revalidateGroupDetail = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['personal-group', groupId] });
		queryClient.invalidateQueries({ queryKey: ['personal-group-users', groupId] });
	}, [queryClient, groupId]);

	const handleLinkUser = useCallback(
		async (linkData: LinkData) => {
			const { userId, roleIds } = linkData;
			if (!userId) return;
			try {
				await request.post(`/api/personal-dashboard/groups/${groupId}/users`, { userId, roleIds });
				EventEmitter.emit('toast', { type: 'success', message: 'User added to company' });
			} catch (error: unknown) {
				const err = error as { message?: string };
				EventEmitter.emit('toast', {
					type: 'error',
					message: err?.message || 'Error adding user to company',
				});
			}
			revalidateGroupDetail();
		},
		[groupId, revalidateGroupDetail],
	);

	const handleInviteUser = useCallback(
		async (data: InviteData) => {
			try {
				const { body: user } = await request.post<User>(`/api/personal-dashboard/groups/${groupId}/users`, data);
				EventEmitter.emit('toast', { type: 'success', message: 'User added to company' });
				revalidateGroupDetail();
				return user;
			} catch (error: unknown) {
				const err = error as { message?: string };
				EventEmitter.emit('toast', {
					type: 'error',
					message: err?.message || 'Error adding user to company',
				});
				throw error;
			}
		},
		[groupId, revalidateGroupDetail],
	);

	const handleDeleteUser = useCallback(
		async (userId: string) => {
			try {
				await request.del(`/api/personal-dashboard/groups/${groupId}/users/${userId}`, {});
				EventEmitter.emit('toast', { type: 'success', message: 'User removed from company' });
			} catch {
				EventEmitter.emit('toast', { type: 'error', message: 'Error removing user from company' });
			}
			revalidateGroupDetail();
		},
		[groupId, revalidateGroupDetail],
	);

	const handleReload = () => {
		revalidateGroupDetail();
	};

	const isLoading = authLoading || configLoading || groupLoading || usersLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!group || !authenticatedUser) return null;

	return (
		<UsersTab
			onInvite={handleInviteUser}
			onLink={handleLinkUser}
			onDelete={handleDeleteUser}
			onReload={handleReload}
			group={group}
			roles={roles || []}
			authenticatedUser={authenticatedUser}
			users={usersData || { data: [], count: 0, page: 0, pageSize: 25, sort: sort || '' }}
			readOnly
			filter={filter}
			permissions={{
				archive: false,
				update: false,
				create: verifyPermission([PERMISSIONS.personalGroupsUsers]),
				canPrintBadges: false,
				canCreateWithoutEmail: false,
				isUserAdmin: false,
				canImport: verifyPermission([PERMISSIONS.personalGroupsUsers]),
			}}
			backUrlLocalStorageKey="personalDashboardUsersBackUrl"
			defaultCountry={defaultCountry}
			importEndpoint={`/api/personal-dashboard/groups/${group.id}/users/import`}
		/>
	);
}
